import {
  Briefcase,
  ChevronRight,
  CreditCard,
  FileSignature,
  Home,
  Smartphone,
  Tags,
  Video,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useOnboardingState } from "@/api/hooks";
import type { OnboardingStep } from "@/api/types";

/** Local-storage key for "user dismissed the checklist".
 *  Per-user, per-browser. Doesn't persist across devices on
 *  purpose — re-orientation on a new device is fine UX. */
const DISMISSED_KEY = "pencilout_onboarding_dismissed";

const ICONS: Record<string, typeof Briefcase> = {
  business_branding: CreditCard,
  pricing_rules: Tags,
  ios_device: Smartphone,
  client: Home,
  walkthrough: Video,
  sent_quote: FileSignature,
  billing_setup: Briefcase,
};

/** Renders a collapsible checklist of post-signup setup steps. The
 *  state is fetched from the server (computed from real data, not a
 *  separate "marked done" flag), so steps auto-tick when the user
 *  completes them. The card auto-hides at 100% complete OR when the
 *  user manually dismisses via the X. */
export function OnboardingChecklist() {
  const { data } = useOnboardingState();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(DISMISSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  // Phone view collapses the card to a single chip. The user can
  // tap to expand into the full list. Default per breakpoint —
  // sm (≥640px) sees the full card, smaller screens see the chip.
  // The sm: classes below handle the responsive part; this state
  // is for the user's tap-to-expand on phone only.
  const [phoneExpanded, setPhoneExpanded] = useState(false);

  if (!data || data.steps.length === 0) return null;
  if (data.percent_complete >= 1.0) return null;
  if (dismissed) return null;
  // Server-side gate — pristine org with no job/client/walkthrough/
  // sent-quote yet → don't render. Prevents the "7 things to do"
  // nag on first sign-in.
  if (!data.should_render) return null;

  // Special-case: pencilout_admin staff get the "all completed"
  // passthrough. The ``percent_complete >= 1.0`` guard above
  // already filters that out — belt + suspenders here.
  if (data.steps.length === 1 && data.steps[0]?.key === "staff") return null;

  const onDismiss = () => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, "true");
    } catch {
      /* localStorage unavailable; just hide for the session */
    }
    setDismissed(true);
  };

  return (
    <div className="rounded-lg border border-pencil-yellow bg-pencil-yellow-soft">
      {/* Header row — chip-style on phone, full card header on
          tablet+. Tapping the title/progress on phone toggles the
          expand state; on tablet+ the body is always shown so the
          tap is a no-op. The dismiss X is its own button so it
          doesn't fight with the chip toggle. */}
      <div className="flex items-center gap-3 p-4">
        <button
          type="button"
          aria-expanded={phoneExpanded}
          onClick={() => setPhoneExpanded((p) => !p)}
          className="flex-1 min-w-0 cursor-pointer text-left sm:cursor-default"
        >
          <h2 className="font-mono text-sm font-bold tracking-tight sm:text-base">
            Get started — {data.completed_count} of {data.total_count} done
          </h2>
          <div className="mt-2">
            <ProgressBar value={data.percent_complete} />
          </div>
        </button>
        {/* Chevron — phone only, indicates expand state. */}
        <span
          className={[
            "flex-none text-graphite-soft transition-transform sm:hidden",
            phoneExpanded ? "rotate-90" : "",
          ].join(" ")}
          aria-hidden
        >
          ›
        </span>
        {/* X dismiss — shown on tablet+ always; on phone only when
            the body is expanded (collapsed chip is one tap-target). */}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss checklist"
          className={[
            "flex-none text-graphite-soft hover:text-graphite",
            phoneExpanded ? "block" : "hidden sm:block",
          ].join(" ")}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {/* Body — list of steps. Phone hides until expanded; tablet+
          always visible. */}
      <div className={["px-4 pb-4 sm:block", phoneExpanded ? "block" : "hidden"].join(" ")}>
        <p className="mb-3 text-xs text-graphite-soft sm:text-sm">
          A quick tour of what to set up. Auto-completes as you go.
        </p>
        <ul className="space-y-2">
          {data.steps.map((step) => (
            <ChecklistRow key={step.key} step={step} />
          ))}
        </ul>
      </div>
    </div>
  );
}

function ProgressBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: progressbars are not interactive controls — WAI-ARIA guidance is they should NOT be focusable; SR users get the value via aria-valuenow on read.
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      className="h-1.5 w-full overflow-hidden rounded bg-paper"
    >
      <div
        style={{ width: `${pct}%` }}
        className="h-full bg-graphite transition-all duration-500"
      />
    </div>
  );
}

function ChecklistRow({ step }: { step: OnboardingStep }) {
  const Icon = ICONS[step.key] ?? Briefcase;
  const completed = step.completed;

  const inner = (
    <>
      <div
        className={[
          "flex h-7 w-7 flex-none items-center justify-center rounded-full border",
          completed
            ? "border-graphite bg-graphite text-paper"
            : "border-rule bg-paper text-graphite-soft",
        ].join(" ")}
      >
        {completed ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-3.5 w-3.5"
            aria-hidden="true"
          >
            <title>Completed</title>
            <path
              fillRule="evenodd"
              d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.41 0z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <Icon className="h-3.5 w-3.5" aria-hidden />
        )}
      </div>
      <div className="flex-1">
        <div
          className={[
            "font-mono text-sm font-medium",
            completed ? "text-graphite-soft line-through" : "text-graphite",
          ].join(" ")}
        >
          {step.label}
        </div>
        {!completed && <div className="text-xs text-graphite-soft">{step.description}</div>}
      </div>
      {!completed && step.cta_path && (
        <ChevronRight className="h-4 w-4 flex-none text-graphite-soft" aria-hidden />
      )}
    </>
  );

  if (!completed && step.cta_path) {
    return (
      <li>
        <Link
          to={step.cta_path}
          className="flex items-start gap-3 rounded p-2 transition hover:bg-paper-warm"
        >
          {inner}
        </Link>
      </li>
    );
  }

  return <li className="flex items-start gap-3 rounded p-2">{inner}</li>;
}
